import { Dispatch, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export interface Road {
  id?: number;
  name: string;
  municipality: string;
}

export interface RoadState {
  road: Road | undefined;
  roads: Road[];
  allRoads: [];
  open: boolean;
  status: "idle" | "loading" | "failed";
}

const initialState: RoadState = {
  road: undefined,
  roads: [],
  allRoads: [],
  open: false,
  status: "idle",
};

export const fetchRoads = createAsyncThunk(
  "roads/fetchRoads",
  async (associationId: number) => {
    const response = await axiosInstance(
      `/roads?associationId=${associationId}`
    );
    const data = await response.data;
    return data;
  }
);

export const fetchAllRoads = createAsyncThunk(
  "roads/fetchAllRoads",
  async () => {
    const response = await axiosInstance("/roads/all");
    return response.data;
  }
);

const roadSlice = createSlice({
  name: "road",
  initialState,
  reducers: {
    setRoad(state, action) {
      state.road = action.payload;
    },
    addRoad(state, action) {
      state.roads.push(action.payload);
    },
    updateRoad(state, action) {
      const roadId = state.roads.findIndex((r) => r.id === action.payload.id);
      state.roads[roadId] = action.payload;
    },
    deleteRoad(state, action) {
      state.roads = state.roads.filter((r: any) => r.id !== action.payload);
    },
    setOpen(state, action) {
      state.open = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRoads.pending, (state) => {
      state.status = "loading";
    });
    builder.addCase(fetchRoads.fulfilled, (state, action) => {
      state.roads = action.payload;
      state.status = "idle";
    });
    builder.addCase(fetchRoads.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(fetchAllRoads.fulfilled, (state, action) => {
      state.allRoads = action.payload;
    });
  },
});

export function setRoad(road: any) {
  return async (dispatch: Dispatch) => {
    dispatch(roadSlice.actions.setRoad(road));
  };
}

export function createRoad(road: any) {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axiosInstance.post(`/roads`, road);
      dispatch(roadSlice.actions.addRoad(response.data));
    } catch (error: any) {
      throw error;
    }
  };
}

export function updateRoad(id: number, road: any) {
  return async (dispatch: Dispatch) => {
    try {
      const response = await axiosInstance.put(`/roads/${id}`, road);
      dispatch(roadSlice.actions.updateRoad(response.data));
    } catch (error: any) {
      throw error;
    }
  };
}

export function deleteRoad(id: number) {
  return async (dispatch: Dispatch) => {
    try {
      await axiosInstance.delete(`/roads/${id}`);
      dispatch(roadSlice.actions.deleteRoad(id));
    } catch (error: any) {
      throw error;
    }
  };
}

export function openRoadForm(open: boolean) {
  return async (dispatch: Dispatch) => {
    dispatch(roadSlice.actions.setOpen(open));
  };
}

export default roadSlice.reducer;
